import React, { useState, useEffect, useCallback } from "react"
import { Modal, Button, Flex } from "antd"
import { TeamOutlined } from "@ant-design/icons"
import styles from "./index.module.css"
import ParentModal from "../Parent"
import MinorModal from "../Minor"
import { SignDocumentModal } from "../SignDocument"
import { useTranslation } from "react-i18next"
import { MultiUser } from "ui/Icon/DSIcon/MultiUser"
import { useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
const options = [
  {
    key: "adult",
    icon: MultiUser,
    label: "Guest Age 18+"
  },
  {
    key: "guardian",
    icon: TeamOutlined,
    label: "Legal Guardian and Minors"
  }
]

export default function ParticipatingModal({
  childCount,
  uuid,
  isShowSignForChildWaiver,
  onCloseParticipating,
  userId,
  waiver,
  loading,
  bookingWaivers,
  tripBookingId,
  visible,
  onClose,
  onNext,
  sharedWaiverId = undefined
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enable_for_adult, enable_for_child } = useSelector((store) => store.digitalWaiver.submissionsAvailable)
  const { guest_data } = useSelector((store) => store.digitalWaiver.waiverSubmissionsByTripBooking)
  const hasUnsignedChild = guest_data?.some((g) => g.participant_type === "Child" && g.status === "Unsigned")

  const [selected, setSelected] = useState(null)
  const [showParentModal, setShowParentModal] = useState(false)
  const [showMinorModal, setShowMinorModal] = useState(false)
  const [showSignDocumentModal, setShowSignDocumentModal] = useState(false)
  const [waiverSubmissionId, setWaiverSubmissionId] = useState(null)
  const [minorData, setMinorData] = useState([])
  const { id: urlId } = useParams()
  // Use explicit sharedWaiverId prop when provided (even null); fall back to URL param
  // for the SharedWaiversRoute where :id IS the shared waiver UUID.
  const id = sharedWaiverId !== undefined ? sharedWaiverId : urlId

  useEffect(() => {
    if (isShowSignForChildWaiver) {
      setSelected("guardian")
    } else if (!isShowSignForChildWaiver) {
      setSelected("adult")
    }
  }, [isShowSignForChildWaiver])
  const handleSignDocumentOpen = useCallback((submissionId, minors) => {
    setWaiverSubmissionId(submissionId)
    setMinorData(minors)
    setShowSignDocumentModal(true)
    setShowMinorModal(false)
  }, [])

  const handleSignDocumentClose = useCallback(() => {
    setShowSignDocumentModal(false)
    setWaiverSubmissionId(null)
    setMinorData([])
    if (onClose) onClose()
  }, [onClose])

  const handleNext = useCallback(() => {
    if (selected === "adult") {
      setShowParentModal(true)
    } else if (selected === "guardian") {
      setShowMinorModal(true)
    } else if (onNext) {
      onNext(selected)
    }
  }, [selected, onNext])
  return (
    <>
      <Modal
        maskClosable={false}
        open={visible}
        onCancel={onClose}
        footer={null}
        closable={true}
        centered
        width={700}
        className={`${styles.modalContainer}`}
      >
        <div className={`mb-4xl ${styles.title}`}>{t("embedded.booking_confirmation.who_is_participation")}</div>
        <div className={`mb-4xl ${styles.optionsRow}`}>
          {options.map((opt) => {
            const IconComp = opt.icon
            const isSelected = selected === opt.key
            const isDisabled =
              (opt.key === "adult" && !enable_for_adult) ||
              (opt.key === "guardian" && (!enable_for_child || !hasUnsignedChild)) ||
              (isShowSignForChildWaiver ? opt.key === "adult" : false)
            const cardClassName = isSelected
              ? `${styles.optionCard} ${styles.optionCardSelected} py-xl px-l`
              : `${styles.optionCard} py-xl px-l`
            const iconClassName = isSelected ? `${styles.optionIcon} ${styles.optionIconSelected}` : styles.optionIcon

            return (
              <div
                key={opt.key}
                className={cardClassName}
                onClick={() => {
                  if (!isDisabled) setSelected(opt.key)
                }}
                style={isDisabled ? { pointerEvents: "none", opacity: 0.5 } : {}}
              >
                <Flex justify="center" align="center" className={iconClassName}>
                  <IconComp />
                </Flex>
                <Flex justify="space-between" align="center" className={`mb-l ml-0 p-s ${styles.multiUserIcon}`}>
                  {/* <MultiUser /> */}
                </Flex>
                <div className={styles.optionLabel}>{opt.label}</div>
              </div>
            )
          })}
        </div>
        <Flex align="center" justify="flex-end">
          <Flex align="center" gap={8}>
            <Button onClick={onClose} color="primary" variant="outlined">
              {t("common.cancel")}
            </Button>

            <Button color="primary" variant="solid" disabled={!selected} onClick={handleNext}>
              {t("embedded.booking_confirmation.next")}
            </Button>
          </Flex>
        </Flex>
      </Modal>
      <ParentModal
        uuid={uuid}
        onClose={onClose}
        userId={userId}
        bookingWaivers={bookingWaivers}
        tripBookingId={tripBookingId}
        id={id}
        selected={selected}
        waiver={waiver}
        loading={loading}
        visible={showParentModal}
        onCancel={() => setShowParentModal(false)}
        onNext={() => setShowParentModal(false)}
        onSignDocumentOpen={handleSignDocumentOpen}
      />
      <MinorModal
       childCount={childCount}
        uuid={uuid}
        onClose={onClose}
        closeModal={() => {
          setShowMinorModal(false)
          // onCancel()
        }}
        userId={userId}
        bookingWaivers={bookingWaivers}
        id={id}
        selected={selected}
        waiver={waiver}
        tripBookingId={tripBookingId}
        loading={loading}
        visible={showMinorModal}
        onCancel={() => setShowMinorModal(false)}
        onNext={() => setShowMinorModal(false)}
        onAddMinor={() => {}}
        onSignDocumentOpen={handleSignDocumentOpen}
      />
      {showSignDocumentModal && (
        <SignDocumentModal
          uuid={uuid}
          waiverSubmissionId={waiverSubmissionId}
          pendingSubmission={minorData}
          bookingWaivers={bookingWaivers}
          id={id}
          selected={selected}
          waiver={waiver}
          loading={loading}
          minorData={minorData}
          open={showSignDocumentModal}
          onCancel={handleSignDocumentClose}
          parentData={minorData}
        />
      )}
    </>
  )
}

ParticipatingModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onNext: PropTypes.func,
  onCloseParticipating: PropTypes.func,
  childCount: PropTypes.number,
  uuid: PropTypes.string,
  isShowSignForChildWaiver: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  waiver: PropTypes.object,
  loading: PropTypes.bool,
  bookingWaivers: PropTypes.array,
  tripBookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sharedWaiverId: PropTypes.string
}
